import React, { useRef, useEffect } from 'react';
import { Video, VideoOff, Mic, MicOff, Monitor, PhoneOff, Minimize2, Maximize2 } from 'lucide-react';
import { useCall } from '../../context/CallContext';
import { useAuth } from '../../context/AuthContext';
import { UserAvatar } from '../common/UserAvatar';

interface VideoTileProps {
  stream: MediaStream | null;
  displayName: string;
  avatarUrl?: string | null | undefined;
  userId?: string | undefined;
  isMuted: boolean;
  isVideoOff: boolean;
  isScreenSharing: boolean;
  isLocal?: boolean | undefined;
  statusText?: string | undefined;
}

const VideoTile: React.FC<VideoTileProps> = ({
  stream,
  displayName,
  avatarUrl,
  userId,
  isMuted,
  isVideoOff,
  isScreenSharing,
  isLocal,
  statusText,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      if (stream && !isVideoOff) {
        if (el.srcObject !== stream) {
          el.srcObject = stream;
        }
        el.play().catch(() => {});
      } else {
        el.srcObject = null;
      }
    }
  }, [stream, isVideoOff]);

  const hasVideo = !isVideoOff && Boolean(stream);

  return (
    <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex items-center justify-center min-h-[180px] max-h-[calc(100vh-200px)] aspect-video w-full shadow-2xl">
      {/* Video Element */}
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full object-cover ${isLocal && !isScreenSharing ? '-scale-x-100' : ''}`}
        />
      ) : (
        /* Avatar Placeholder when video is disabled, connecting, or ringing */
        <div className="flex flex-col items-center gap-3 p-6 select-none">
          <div className="relative">
            {statusText && (
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
            )}
            <UserAvatar
              userId={userId}
              displayName={displayName}
              avatarUrl={avatarUrl}
              size="2xl"
              className="relative shadow-xl ring-4 ring-zinc-800"
            />
          </div>
          <div className="text-center space-y-0.5">
            <span className="text-sm text-zinc-300 font-semibold block">{displayName}</span>
            {statusText && (
              <span className="text-xs text-emerald-400 font-medium animate-pulse block">{statusText}</span>
            )}
          </div>
        </div>
      )}

      {/* Top Badges */}
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <span className="bg-black/60 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-lg border border-white/10 shadow-sm">
          {displayName} {isLocal && '(You)'}
        </span>
        {isScreenSharing && (
          <span className="bg-emerald-500/90 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-lg flex items-center gap-1.5 shadow-sm">
            <Monitor className="h-3.5 w-3.5" /> Sharing Screen
          </span>
        )}
      </div>

      {/* Mute Badge */}
      {isMuted && (
        <div className="absolute bottom-3 right-3 bg-red-500/90 text-white p-2 rounded-full shadow-lg z-10 animate-in fade-in">
          <MicOff className="h-4 w-4" />
        </div>
      )}
    </div>
  );
};

interface AudioTileProps {
  displayName: string;
  avatarUrl?: string | null | undefined;
  userId?: string | undefined;
  isMuted: boolean;
  isSpeaking?: boolean;
  isLocal?: boolean | undefined;
  statusText?: string | undefined;
}

const AudioTile: React.FC<AudioTileProps> = ({
  displayName,
  avatarUrl,
  userId,
  isMuted,
  isSpeaking,
  isLocal,
  statusText,
}) => {
  return (
    <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-8 flex flex-col items-center justify-center space-y-5 shadow-2xl relative min-w-[220px] max-w-sm flex-1 select-none">
      <div className="relative">
        {(!isMuted && isSpeaking) || statusText ? (
          <div className="absolute inset-0 rounded-full bg-emerald-500/25 animate-ping" />
        ) : null}
        <UserAvatar
          userId={userId}
          displayName={displayName}
          avatarUrl={avatarUrl}
          size="2xl"
          className="relative shadow-2xl ring-4 ring-zinc-800"
        />
        {isMuted && (
          <div className="absolute -bottom-1 -right-1 bg-red-500 text-white p-2 rounded-full shadow-lg z-10">
            <MicOff className="h-4 w-4" />
          </div>
        )}
      </div>

      <div className="text-center space-y-1">
        <h4 className="font-bold text-base text-white">{displayName} {isLocal && '(You)'}</h4>
        <p className="text-xs text-zinc-400 font-medium flex items-center justify-center gap-1.5">
          {!isMuted && isSpeaking && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
          <span>{statusText || (isMuted ? 'Muted' : isSpeaking ? 'Speaking...' : 'Connected')}</span>
        </p>
      </div>
    </div>
  );
};

export const ActiveCallOverlay: React.FC = () => {
  const { user } = useAuth();
  const {
    callState,
    activeCall,
    localStream,
    remoteStream,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    audioLevel,
    durationSeconds,
    endCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
  } = useCall();

  const [isMinimized, setIsMinimized] = React.useState(false);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Guarantee remote audio stream plays reliably
  useEffect(() => {
    if (remoteStream && remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.volume = 1.0;
      remoteAudioRef.current.muted = false;
      remoteAudioRef.current.play().catch(() => {});
    }
  }, [remoteStream]);

  if (callState === 'IDLE' || callState === 'INCOMING_RINGING' || !activeCall) {
    return null;
  }

  const isVideoCall = activeCall.callType === 'VIDEO';
  const isOutgoingRinging = callState === 'OUTGOING_RINGING';
  const isCaller = user?.id?.toLowerCase() === activeCall.callerId?.toLowerCase();
  const peerName = isCaller
    ? (activeCall.recipientName || 'Collaborator')
    : (activeCall.callerName || 'Collaborator');
  const peerAvatar = isCaller
    ? activeCall.recipientAvatar
    : activeCall.callerAvatar;
  const peerId = isCaller ? activeCall.recipientId : activeCall.callerId;

  const formatDuration = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const remainingSecs = sec % 60;
    return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const participantCount = isOutgoingRinging ? '1/2 Participants' : '2 Participants';

  // ─── Active & Outgoing Call View (Unified Channel-like Fullscreen Grid) ────
  return (
    <div
      className={`fixed z-50 transition-all duration-300 select-none ${
        isMinimized
          ? 'bottom-6 right-6 w-96 h-60 rounded-3xl shadow-2xl border border-zinc-800 bg-zinc-950 overflow-hidden flex flex-col p-3'
          : 'inset-0 bg-zinc-950 flex flex-col p-4 overflow-hidden'
      }`}
    >
      {/* Hidden audio element to guarantee remote audio playback */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {/* Header Bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 bg-zinc-900/60 backdrop-blur-md rounded-xl border border-zinc-800 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-bold text-sm text-white">
            Direct {isVideoCall ? 'Video' : 'Audio'} Call ({participantCount})
          </span>
          {isOutgoingRinging ? (
            <span className="text-xs bg-primary/20 text-primary px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1.5 animate-pulse">
              <span>Calling {peerName}...</span>
            </span>
          ) : (
            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2.5 py-0.5 rounded-full font-mono font-medium flex items-center gap-1.5">
              <span>{callState === 'CONNECTED' ? formatDuration(durationSeconds) : 'Connecting...'}</span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? 'Expand Call Window' : 'Minimize Call Window'}
            className="p-2 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white border border-zinc-700 transition"
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Main Viewport: Video Grid or Audio Huddle Cards */}
      <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center py-2">
        {isVideoCall || isScreenSharing ? (
          /* Video Grid Mode: Balanced side-by-side tiles for Local and Remote peers */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl w-full max-h-full overflow-y-auto px-2">
            {/* Local Participant Tile */}
            <VideoTile
              stream={isScreenSharing && screenStream ? screenStream : localStream}
              displayName={user?.displayName || 'You'}
              avatarUrl={user?.avatarUrl}
              userId={user?.id}
              isMuted={isMuted}
              isVideoOff={isCameraOff && !isScreenSharing}
              isScreenSharing={isScreenSharing}
              isLocal
            />

            {/* Remote Participant Tile */}
            <VideoTile
              stream={isOutgoingRinging ? null : remoteStream}
              displayName={peerName}
              avatarUrl={peerAvatar}
              userId={peerId}
              isMuted={false}
              isVideoOff={isOutgoingRinging || !remoteStream}
              isScreenSharing={false}
              statusText={isOutgoingRinging ? `Calling ${peerName}...` : undefined}
            />
          </div>
        ) : (
          /* Audio Huddle Mode: Balanced participant cards for Local and Remote peers */
          <div className="flex flex-wrap items-center justify-center gap-6 max-w-3xl w-full p-4">
            {/* Local Participant Card */}
            <AudioTile
              displayName={user?.displayName || 'You'}
              avatarUrl={user?.avatarUrl}
              userId={user?.id}
              isMuted={isMuted}
              isSpeaking={!isMuted && audioLevel > 15}
              isLocal
            />

            {/* Remote Participant Card */}
            <AudioTile
              displayName={peerName}
              avatarUrl={peerAvatar}
              userId={peerId}
              isMuted={false}
              isSpeaking={callState === 'CONNECTED'}
              statusText={isOutgoingRinging ? `Calling ${peerName}...` : undefined}
            />
          </div>
        )}
      </div>

      {/* Bottom Floating Control Toolbar */}
      {!isMinimized && (
        <div className="flex-shrink-0 flex items-center justify-center gap-4 pt-2 pb-1 z-20">
          <div className="flex items-center gap-3 px-6 py-2.5 bg-zinc-900/90 border border-zinc-800 backdrop-blur-md rounded-2xl shadow-2xl">
            {/* Mute/Unmute */}
            <button
              onClick={toggleMute}
              title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
              className={`p-3 rounded-xl transition ${
                isMuted ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>

            {/* Camera On/Off (Video Calls only) */}
            {isVideoCall && (
              <button
                onClick={toggleCamera}
                title={isCameraOff ? 'Turn Camera On' : 'Turn Camera Off'}
                className={`p-3 rounded-xl transition ${
                  isCameraOff ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
              >
                {isCameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
              </button>
            )}

            {/* Screen Share (Video Calls or when active) */}
            {(isVideoCall || isScreenSharing) && (
              <button
                onClick={toggleScreenShare}
                title={isScreenSharing ? 'Stop Screen Share' : 'Share Screen'}
                className={`p-3 rounded-xl transition ${
                  isScreenSharing ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
              >
                <Monitor className="h-5 w-5" />
              </button>
            )}

            {/* Leave / End / Cancel Call */}
            <button
              onClick={endCall}
              title={isOutgoingRinging ? 'Cancel Call' : 'Leave / End Call'}
              className="p-3 rounded-xl bg-red-600 text-white hover:bg-red-700 transition font-bold flex items-center gap-1.5"
            >
              <PhoneOff className="h-5 w-5" />
              {isOutgoingRinging && <span className="text-xs font-semibold pr-1">Cancel</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


